import { useEffect } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { PageViewedTracker } from '@zenbusiness/application-commons-clickstream';
import PageLoading from '@zenbusiness/zui/PageLoading';

import { BANKING_TRANSITION_DEEP_LINK_PAGE_NAME, DEEP_LINK_PATH } from './constants';
import useBankingTransitionTarget from './useBankingTransitionTarget';

/**
 * Resolves which business entity holds the banking account and forwards the customer into that
 * entity's transition flow. The destinations live in dashboard-ui rather than this MFE, so the
 * redirect is a full page navigation and not a router navigate.
 *
 * Deliberately not gated on `zb-banking-sunset-ph3`: Marketing's emails point here, so the route
 * has to resolve whether or not the flag is on. `BankingTransitionLayout` owns the flag-off
 * redirect, which sends the customer to their banking dashboard.
 * @returns {import('react').ReactElement} A loading screen shown until the redirect fires
 */
export default function BankingTransitionDeepLink() {
  const { authenticated, login } = useAuthentication();
  const { target, loading: isResolvingEntity } = useBankingTransitionTarget();

  const redirectTo = authenticated && !isResolvingEntity ? target : null;

  useEffect(() => {
    if (!authenticated) {
      // Keep Marketing's campaign parameters across the login hop.
      login(`${DEEP_LINK_PATH}${window.location.search}`);
      return;
    }

    if (redirectTo) {
      // replace, not assign: Back would otherwise land here and redirect forward again.
      window.location.replace(redirectTo);
    }
  }, [authenticated, login, redirectTo]);

  return (
    <>
      {authenticated && (
        <PageViewedTracker
          appPageName={BANKING_TRANSITION_DEEP_LINK_PAGE_NAME}
          appSection="banking"
          experienceOrigin="dashboard"
        />
      )}
      <PageLoading />
    </>
  );
}
